'use client';

import { useAuiState } from '@assistant-ui/react';
import { type JSX } from 'react';

import { Icon } from '@videoblocks/design-system';

import { formatToolName } from '../../_utils';

/**
 * Fallback component for rendering tool calls.
 * Shows a minimal indicator that a tool was called.
 */
export function ToolFallback(): JSX.Element | null {
    // Use granular selectors to avoid unnecessary re-renders when unrelated part properties change
    const partType = useAuiState(({ part }) => part.type);
    const status = useAuiState(({ part }) => (part.type === 'tool-call' ? part.status : null));
    const toolName = useAuiState(({ part }) => (part.type === 'tool-call' ? part.toolName : null));

    if (partType !== 'tool-call' || !status) {
        return null;
    }

    const isLoading = status.type === 'running';
    const isComplete = status.type === 'complete';
    const isError = status.type === 'incomplete' && status.reason === 'error';

    const displayName = formatToolName(toolName || 'Tool');

    return (
        <div className="my-2 flex items-center gap-2 text-xs text-gray-500">
            {isLoading && (
                <>
                    <svg className="h-3 w-3 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                    </svg>
                    <span>Running {displayName}...</span>
                </>
            )}
            {isComplete && (
                <>
                    <svg
                        width="11"
                        height="8"
                        viewBox="0 0 11 8"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className="[&_path]:stroke-gray-400"
                    >
                        <path
                            d="M9.75 0.75L3.75 6.75L0.75 3.75"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                    <span>{displayName} completed</span>
                </>
            )}
            {isError && (
                <>
                    <Icon name="error" className="h-3 w-3 text-red-500" />
                    <span>{displayName} failed</span>
                </>
            )}
        </div>
    );
}
